import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, RefreshCw, Stethoscope, PhoneCall, Send, CheckCircle2, XCircle, Clock, Copy, Mail, ShieldAlert, Trash2, Pencil } from "lucide-react";
import CallButton from "@/components/admin/CallButton";

/* Refill requests PawLinx raised with a customer's own vet.
   The queue exists so a promise made in an email, that we would call the clinic, is something
   the business can actually see and act on rather than remember. */

const STATUS = {
  pending_review: { label: "Waiting for you to approve", cls: "bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/25" },
  not_sent:   { label: "Not sent, discarded",   cls: "bg-black/5 dark:bg-white/10 text-paw-text-muted dark:text-white/40 border-paw-border dark:border-white/10" },
  emailed:    { label: "Waiting on the clinic", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/25" },
  called:     { label: "Called",                cls: "bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/25" },
  authorized: { label: "Authorized",            cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/25" },
  declined:   { label: "Declined",              cls: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/25" },
  closed:     { label: "Closed",                cls: "bg-black/5 dark:bg-white/10 text-paw-text-muted dark:text-white/40 border-paw-border dark:border-white/10" },
};

const when = (v) => {
  if (!v) return "";
  try { return new Date(v).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return String(v).slice(0, 16); }
};

function Card({ title, icon: Icon, children, testid, right }) {
  return (
    <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon ? <Icon className="w-4 h-4 text-paw-primary" /> : null}
        <h3 className="font-outfit font-bold text-[15px] text-paw-text dark:text-white/90">{title}</h3>
        {right ? <div className="ml-auto">{right}</div> : null}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between gap-4 py-1">
      <span className="font-manrope text-xs text-paw-text-secondary dark:text-white/50">{label}</span>
      <span className="font-manrope text-xs font-semibold text-paw-text dark:text-white/90 text-right break-words">{value}</span>
    </div>
  );
}

export default function AdminRefillRequests() {
  const [rows, setRows] = useState([]);
  const [callTasks, setCallTasks] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [tellCustomer, setTellCustomer] = useState(true);
  const [filter, setFilter] = useState("open");
  /* Nothing about a vet or a price leaves without a person reading it, so the queue of held
     emails sits at the top of this page rather than somewhere it could be missed. */
  const [outbox, setOutbox] = useState([]);
  const [obSel, setObSel] = useState(null);
  const [obSubject, setObSubject] = useState("");
  const [obTo, setObTo] = useState("");
  const [obClinic, setObClinic] = useState("");
  const [obPhone, setObPhone] = useState("");
  const [obReason, setObReason] = useState("");
  const [obBusy, setObBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, ob] = await Promise.all([
        api.get("/admin/refill-requests"),
        api.get("/admin/email-outbox?status=pending").catch(() => ({ data: { rows: [] } })),
      ]);
      setRows(data?.rows || []);
      setCallTasks(data?.call_tasks || 0);
      setOutbox(ob?.data?.rows || []);
      setSel((cur) => (cur ? (data?.rows || []).find(r => r.request_id === cur.request_id) || null : null));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markCalled = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await api.post(`/admin/refill-requests/${sel.request_id}/called`,
                     { note: note.trim(), tell_customer: tellCustomer });
      toast.success(tellCustomer ? "Logged, and the customer has been emailed." : "Logged.");
      setNote(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    if (!sel) return;
    setBusy(true);
    try {
      await api.post(`/admin/refill-requests/${sel.request_id}/resend`);
      toast.success("Request resent to the clinic.");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const openOutbox = (o) => {
    const req = rows.find(r => r.request_id === o.request_id) || {};
    setObSel(o);
    setObSubject(o.preview_subject || "");
    setObTo(o.to || "");
    setObClinic(req.clinic_name || o.context?.clinic_name || "");
    setObPhone(req.clinic_phone || o.context?.clinic_phone || "");
    setObReason("");
  };

  const obSend = async () => {
    if (!obSel) return;
    setObBusy(true);
    try {
      const { data } = await api.post(`/admin/email-outbox/${obSel.outbox_id}/send`, {
        subject: obSubject, to: obTo, clinic_name: obClinic, clinic_phone: obPhone,
      });
      toast.success(`Sent to ${data?.sent_to}`);
      setObSel(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setObBusy(false); }
  };

  const obDiscard = async () => {
    if (!obSel) return;
    setObBusy(true);
    try {
      await api.post(`/admin/email-outbox/${obSel.outbox_id}/discard`, { reason: obReason.trim() });
      toast.success("Discarded. Nothing was sent.");
      setObSel(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setObBusy(false); }
  };

  const shown = rows.filter(r => filter === "all" ? true
    : filter === "open" ? !r.responded_at
    : filter === "answered" ? !!r.responded_at : true);

  return (
    /* The layout's <main> has no padding of its own, so every admin page supplies the same
       page gutter. Without it the content sits flush against the sidebar and the top edge. */
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-7xl space-y-5" data-testid="admin-refill-requests">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <h1 className="font-outfit font-bold text-2xl text-paw-text dark:text-white/90">Vet refill requests</h1>
          <p className="font-manrope text-sm text-paw-text-secondary dark:text-white/50 mt-1 max-w-2xl">
            Customers who have run out of refills and asked us to contact their vet. We told them in writing
            that we would call the clinic if there is no reply in three days, so anything marked call due is a
            promise waiting to be kept.
          </p>
        </div>
        <button onClick={load} className="h-10 px-4 rounded-lg border border-paw-border dark:border-white/10 text-paw-text dark:text-white/80 font-manrope text-sm font-semibold inline-flex items-center gap-2 hover:border-paw-primary/40" data-testid="rr-refresh">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {outbox.length > 0 && (
        <div className="bg-white dark:bg-[#1a1d27] border-2 border-purple-500/40 rounded-2xl p-5" data-testid="rr-outbox">
          <div className="flex items-center gap-2 mb-1">
            <ShieldAlert className="w-4 h-4 text-purple-600 dark:text-purple-300" />
            <h2 className="font-outfit font-bold text-[16px] text-paw-text dark:text-white/90">
              {outbox.length} email{outbox.length === 1 ? "" : "s"} waiting for you to approve
            </h2>
          </div>
          <p className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mb-4 leading-relaxed">
            Reminders and confirmations send themselves. Anything that writes to a clinic, repeats
            what a vet said, or quotes a price waits here until you read it and press send.
          </p>

          <div className="space-y-2">
            {outbox.map(o => (
              <div key={o.outbox_id} className="border border-paw-border dark:border-white/10 rounded-xl overflow-hidden">
                <button onClick={() => (obSel?.outbox_id === o.outbox_id ? setObSel(null) : openOutbox(o))}
                  className="w-full text-left px-4 py-3 hover:bg-black/[0.02] dark:hover:bg-white/5 transition-colors" data-testid="rr-outbox-row">
                  <div className="flex items-start gap-3">
                    <span className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-600 dark:text-purple-300 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-manrope text-sm font-bold text-paw-text dark:text-white/90 truncate">{o.preview_subject}</div>
                      <div className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mt-0.5 truncate">
                        to {o.to} &middot; {o.label}
                      </div>
                      <div className="font-manrope text-[11px] text-purple-600 dark:text-purple-300 mt-1">{o.reason}</div>
                    </div>
                  </div>
                </button>

                {obSel?.outbox_id === o.outbox_id && (
                  <div className="border-t border-paw-border dark:border-white/10 p-4 bg-black/[0.015] dark:bg-white/[0.02]" data-testid="rr-outbox-open">
                    <p className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mb-3 leading-relaxed">
                      The customer typed these from memory, so check them before this goes out.
                      Corrections are saved back onto the request.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block font-manrope text-xs font-semibold text-paw-text dark:text-white/80 mb-1">Send to</label>
                        <input value={obTo} onChange={(e) => setObTo(e.target.value)} type="email"
                          className="w-full h-10 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 px-3 font-manrope text-sm text-paw-text dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-paw-primary/40"
                          data-testid="rr-outbox-to" />
                      </div>
                      <div>
                        <label className="block font-manrope text-xs font-semibold text-paw-text dark:text-white/80 mb-1">Clinic name</label>
                        <input value={obClinic} onChange={(e) => setObClinic(e.target.value)}
                          className="w-full h-10 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 px-3 font-manrope text-sm text-paw-text dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-paw-primary/40"
                          data-testid="rr-outbox-clinic" />
                      </div>
                      <div>
                        <label className="block font-manrope text-xs font-semibold text-paw-text dark:text-white/80 mb-1">Clinic phone</label>
                        <input value={obPhone} onChange={(e) => setObPhone(e.target.value)}
                          className="w-full h-10 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 px-3 font-manrope text-sm text-paw-text dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-paw-primary/40"
                          data-testid="rr-outbox-phone" />
                      </div>
                      <div>
                        <label className="block font-manrope text-xs font-semibold text-paw-text dark:text-white/80 mb-1">Subject</label>
                        <input value={obSubject} onChange={(e) => setObSubject(e.target.value)}
                          className="w-full h-10 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 px-3 font-manrope text-sm text-paw-text dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-paw-primary/40"
                          data-testid="rr-outbox-subject" />
                      </div>
                    </div>

                    <div className="mt-3 rounded-lg border border-paw-border dark:border-white/10 bg-white overflow-hidden">
                      <div className="px-3 py-1.5 border-b border-paw-border font-manrope text-[11px] text-paw-text-muted">
                        Exactly what they will receive
                      </div>
                      <div className="max-h-[420px] overflow-y-auto" data-testid="rr-outbox-preview"
                        dangerouslySetInnerHTML={{ __html: o.preview_html }} />
                    </div>

                    <div className="mt-3">
                      <label className="block font-manrope text-xs font-semibold text-paw-text dark:text-white/80 mb-1">
                        Reason, only needed if you discard it
                      </label>
                      <input value={obReason} onChange={(e) => setObReason(e.target.value)}
                        placeholder="For example, the customer gave the wrong clinic"
                        className="w-full h-10 rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 px-3 font-manrope text-sm text-paw-text dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-paw-primary/40"
                        data-testid="rr-outbox-reason" />
                    </div>

                    <div className="flex flex-wrap gap-2 mt-4">
                      <button onClick={obSend} disabled={obBusy}
                        className="h-10 px-4 rounded-lg bg-paw-success hover:brightness-95 disabled:opacity-60 text-white font-manrope text-sm font-semibold inline-flex items-center gap-2 transition-all" data-testid="rr-outbox-send">
                        {obBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send it
                      </button>
                      <button onClick={obDiscard} disabled={obBusy}
                        className="h-10 px-4 rounded-lg border border-paw-error/40 text-paw-error font-manrope text-sm font-semibold inline-flex items-center gap-2 hover:bg-paw-error/5 disabled:opacity-60" data-testid="rr-outbox-discard">
                        <Trash2 className="w-4 h-4" /> Discard
                      </button>
                      <span className="font-manrope text-[11px] text-paw-text-muted dark:text-white/30 self-center">
                        Discarding marks the request as not sent. Nothing reaches the clinic.
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {callTasks > 0 && (
        <div className="flex items-start gap-3 rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3" data-testid="rr-call-banner">
          <PhoneCall className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="font-manrope text-sm text-amber-800 dark:text-amber-300">
            <strong>{callTasks}</strong> clinic{callTasks === 1 ? "" : "s"}need to be called. They have not replied
            within three days of our email.
          </p>
        </div>
      )}

      <div className="flex gap-2">
        {[["open", "Open"], ["answered", "Answered"], ["all", "All"]].map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)}
            className={`h-9 px-4 rounded-lg font-manrope text-sm font-semibold transition-colors ${filter === k ? "bg-paw-primary text-white" : "border border-paw-border dark:border-white/10 text-paw-text-secondary dark:text-white/60"}`}
            data-testid={`rr-filter-${k}`}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="py-16 text-center text-paw-text-muted dark:text-white/40"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : shown.length === 0 ? (
        <div className="bg-white dark:bg-[#1a1d27] border border-paw-border dark:border-white/5 rounded-2xl p-10 text-center font-manrope text-sm text-paw-text-muted dark:text-white/40">
          Nothing here.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,420px)] gap-5 items-start">
          <div className="space-y-2">
            {shown.map(r => {
              const st = STATUS[r.status] || STATUS.emailed;
              const active = sel?.request_id === r.request_id;
              return (
                <button key={r.request_id} onClick={() => setSel(r)}
                  className={`w-full text-left bg-white dark:bg-[#1a1d27] border rounded-2xl p-4 transition-colors ${active ? "border-paw-primary" : "border-paw-border dark:border-white/5 hover:border-paw-primary/40"}`}
                  data-testid="rr-row">
                  <div className="flex items-start gap-3">
                    <span className="w-9 h-9 rounded-lg bg-paw-primary/10 text-paw-primary flex items-center justify-center flex-shrink-0">
                      <Stethoscope className="w-4 h-4" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-manrope text-sm font-bold text-paw-text dark:text-white/90 truncate">{r.medication}</div>
                      <div className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mt-0.5 truncate">
                        {r.full_name} &middot; {r.pet_name || "pet"} &middot; {r.clinic_name}
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full border font-manrope text-[11px] font-bold ${st.cls}`}>{st.label}</span>
                        {r.call_due && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/25 font-manrope text-[11px] font-bold">
                            <PhoneCall className="w-3 h-3" /> Call due
                          </span>
                        )}
                        <span className="font-manrope text-[11px] text-paw-text-muted dark:text-white/30">{when(r.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="space-y-4 lg:sticky lg:top-4">
            {!sel ? (
              <Card title="Pick a request" testid="rr-empty">
                <p className="font-manrope text-xs text-paw-text-secondary dark:text-white/50">
                  Choose one on the left to see the full file and act on it.
                </p>
              </Card>
            ) : (
              <>
                <Card title="The request" icon={Stethoscope} testid="rr-detail">
                  <Row label="Customer" value={sel.full_name} />
                  <Row label="Email" value={sel.email} />
                  <Row label="Phone" value={sel.phone} />
                  <Row label="Pet" value={[sel.pet_name, sel.pet_type].filter(Boolean).join(", ")} />
                  <Row label="Medication" value={sel.medication} />
                  <Row label="Requested" value={sel.quantity_label} />
                  <Row label="Order reference" value={sel.order_ref} />
                  <Row label="Customer note" value={sel.customer_note} />
                  <Row label="Raised" value={when(sel.created_at)} />
                  <Row label="Link expires" value={when(sel.expires_at)} />
                  {sel.phone ? (
                    <div className="mt-3 pt-3 border-t border-paw-border dark:border-white/5">
                      <CallButton to={sel.phone} who={sel.full_name} purpose="customer"
                        requestId={sel.request_id} onFinished={load} />
                    </div>
                  ) : null}
                </Card>

                <Card title="The clinic" icon={Mail} testid="rr-clinic">
                  <Row label="Name" value={sel.clinic_name} />
                  <Row label="Email" value={sel.clinic_email} />
                  <Row label="Phone" value={sel.clinic_phone} />
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button onClick={() => { navigator.clipboard?.writeText(sel.form_url); toast.success("Link copied"); }}
                      className="h-9 px-3 rounded-lg border border-paw-border dark:border-white/10 font-manrope text-xs font-semibold text-paw-text dark:text-white/80 inline-flex items-center gap-1.5 hover:border-paw-primary/40" data-testid="rr-copy-link">
                      <Copy className="w-3.5 h-3.5" /> Copy their form link
                    </button>
                    <button onClick={resend} disabled={busy || !!sel.responded_at}
                      className="h-9 px-3 rounded-lg border border-paw-border dark:border-white/10 font-manrope text-xs font-semibold text-paw-text dark:text-white/80 inline-flex items-center gap-1.5 hover:border-paw-primary/40 disabled:opacity-40" data-testid="rr-resend">
                      <Send className="w-3.5 h-3.5" /> Resend the email
                    </button>
                  </div>
                  {sel.clinic_phone ? (
                    <div className="mt-3 pt-3 border-t border-paw-border dark:border-white/5">
                      <CallButton to={sel.clinic_phone} who={sel.clinic_name} purpose="clinic"
                        requestId={sel.request_id} onFinished={load} />
                      <p className="mt-2 font-manrope text-[11px] text-paw-text-muted dark:text-white/30 leading-relaxed">
                        PawLinx rings your own phone first, then dials the clinic. They see the
                        PawLinx number, never yours.
                      </p>
                    </div>
                  ) : null}
                </Card>

                {sel.responded_at ? (
                  <Card title="What the clinic said" icon={sel.decision === "declined" ? XCircle : CheckCircle2} testid="rr-response">
                    <Row label="Decision" value={sel.decision === "declined" ? "Not authorized" : sel.decision === "authorized_amended" ? "Authorized with changes" : "Authorized"} />
                    <Row label="Prescriber" value={sel.prescriber?.name} />
                    <Row label="License" value={sel.prescriber?.license} />
                    <Row label="Clinic phone given" value={sel.prescriber?.phone} />
                    <Row label="Assistant" value={sel.prescriber?.assistant} />
                    <Row label="Medication" value={sel.authorized_medication} />
                    <Row label="Quantity" value={sel.authorized_quantity} />
                    <Row label="Directions" value={sel.sig} />
                    <Row label="Refills" value={sel.refills_authorized} />
                    <Row label="Reason given" value={sel.decline_reason} />
                    <Row label="Answered" value={when(sel.responded_at)} />
                  </Card>
                ) : (
                  <Card title="Call the clinic" icon={PhoneCall} testid="rr-call">
                    <p className="font-manrope text-xs text-paw-text-secondary dark:text-white/50 mb-3 leading-relaxed">
                      {sel.call_due
                        ? "Three days have passed with no reply. Call them, then log it here."
                        : "You can call at any time. This just records that you did."}
                      {sel.called_at ? ` Last logged ${when(sel.called_at)}.` : ""}
                    </p>
                    <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3}
                      placeholder="What happened on the call"
                      className="w-full rounded-lg bg-white dark:bg-[#12141c] border border-paw-border dark:border-white/10 px-3 py-2 font-manrope text-sm text-paw-text dark:text-white/90 focus:outline-none focus:ring-2 focus:ring-paw-primary/40"
                      data-testid="rr-call-note" />
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                      <input type="checkbox" checked={tellCustomer} onChange={(e) => setTellCustomer(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-paw-primary" data-testid="rr-tell-customer" />
                      <span className="font-manrope text-xs text-paw-text-secondary dark:text-white/60">
                        Email the customer to say we are calling their vet
                      </span>
                    </label>
                    <button onClick={markCalled} disabled={busy}
                      className="mt-3 w-full h-10 rounded-lg bg-paw-primary hover:bg-paw-primary-hover disabled:opacity-60 text-white font-manrope text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors" data-testid="rr-log-call">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><PhoneCall className="w-4 h-4" /> Log the call</>}
                    </button>
                  </Card>
                )}

                <Card title="History" icon={Clock} testid="rr-timeline">
                  <ol className="space-y-2">
                    {(sel.timeline || []).map((t, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="w-1.5 h-1.5 rounded-full bg-paw-primary mt-1.5 flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="font-manrope text-xs text-paw-text dark:text-white/80">{t.what}</div>
                          <div className="font-manrope text-[11px] text-paw-text-muted dark:text-white/30">{when(t.at)}</div>
                        </div>
                      </li>
                    ))}
                    {(sel.timeline || []).length === 0 && (
                      <li className="font-manrope text-xs text-paw-text-muted dark:text-white/30">Nothing logged yet.</li>
                    )}
                  </ol>
                </Card>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
